import React, { useState, useEffect } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db, auth } from './firebaseConfig';
import { doc, onSnapshot, collection, query, where } from 'firebase/firestore';
import './LinkedInTheme.css';

function HmTaskPage() {
  const { t } = useTranslation();
  const { jdId } = useParams();
  const { userProfile, createdJds } = useOutletContext() || {}; // Get profile and createdJds from layout context
  const [jd, setJd] = useState(null);
  const [error, setError] = useState(null);
  const [isLaunching, setIsLaunching] = useState(false);
  const [simulations, setSimulations] = useState([]);
  const [jdStatuses, setJdStatuses] = useState({});

  const defaultAvatar = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0iI2NjYyI+PHBhdGggZD0iTTEyIDEyYzIuMjEgMC00LTEuNzktNC00czEuNzktNCA0LTRzNCAxLjc5IDQgNHMtMS43OSA0LTQgNHptMCAyYy0yLjY3IDAtOCAxLjM0LTggNHYyaDE2di0yYy04LTIuNjYtNS4zMy00LTgtNHoiLz48L3N2Zz4=";

  useEffect(() => {
    if (!jdId) {
      setError(t('teamMemberTaskPage.errorNoJdId'));
      return;
    }

    const jdDocRef = doc(db, 'jobDescriptions', jdId);
    const unsubscribeJd = onSnapshot(jdDocRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        if (auth.currentUser && data.createdBy === auth.currentUser.uid) {
          setJd({ id: doc.id, ...data });
        } else {
          setError("You do not have permission to view this page.");
        }
      } else {
        setError(t('teamMemberTaskPage.errorNotFound'));
      }
    }, (err) => {
      console.error("Error fetching document:", err);
      setError(t('teamMemberTaskPage.errorFetch'));
    });

    // Fetch all JDs created by the user and their latest simulation status
    const qJds = query(collection(db, 'jobDescriptions'), where('createdBy', '==', auth.currentUser.uid));
    const unsubscribeAllJds = onSnapshot(qJds, async (querySnapshot) => {
      const allJds = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const statuses = {};

      for (const singleJd of allJds) {
        const qSims = query(collection(db, 'simulations'), where('jd_id', '==', singleJd.id));
        const simSnapshot = await new Promise(resolve => onSnapshot(qSims, resolve));
        if (!simSnapshot.empty) {
          // Get the most recent simulation status
          const latestSim = simSnapshot.docs.map(doc => doc.data()).sort((a, b) => (b.created_at?.toDate() || new Date(0)) - (a.created_at?.toDate() || new Date(0)))[0];
          statuses[singleJd.id] = latestSim.status;
        }
      }
      setJdStatuses(statuses);
    }, (err) => {
      console.error("Error fetching all JDs for status:", err);
    });


    const simulationsRef = collection(db, 'simulations');
    const q = query(simulationsRef, where('jd_id', '==', jdId));
    const unsubscribeSimulations = onSnapshot(q, (querySnapshot) => {
      const simsData = [];
      querySnapshot.forEach((doc) => {
        simsData.push({ id: doc.id, ...doc.data() });
      });
      // Sort by date, newest first
      simsData.sort((a, b) => {
          const dateA = a.created_at?.toDate ? a.created_at.toDate() : new Date(0);
          const dateB = b.created_at?.toDate ? b.created_at.toDate() : new Date(0);
          return dateB - dateA;
      });
      setSimulations(simsData);
    }, (err) => {
      console.error("Error fetching simulations:", err);
    });

    return () => {
      unsubscribeJd();
      unsubscribeSimulations();
      unsubscribeAllJds();
    };
  }, [jdId, t]);

  const handleLaunchSimulation = async () => {
    if (!jd) {
      setError("Cannot launch without JD content.");
      return;
    }
    setIsLaunching(true);
    setError(null);
    try {
      const triggerUrl = process.env.REACT_APP_SIMULATION_TRIGGER_URL;
      const orchestratorUrlSuffix = process.env.REACT_APP_ORCHESTRATOR_URL_SUFFIX;

      if (!triggerUrl || !orchestratorUrlSuffix) {
        throw new Error("Application is not configured correctly. Missing trigger or orchestrator URL suffix.");
      }

      const orchestratorUrl = `https://pod-plus-orchestrator${orchestratorUrlSuffix}`;

      const payload = {
        orchestrator_url: orchestratorUrl,
        sim_mode: 'search',
        jd_id: jdId,
      };

      const response = await fetch(`${triggerUrl}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to launch simulation.');
      }

    } catch (err) {
      setError(err.message);
    }
    finally {
      setIsLaunching(false);
    }
  };

  const handleGenerateReport = (simId, vtcId) => {
    const orchestratorUrlSuffix = process.env.REACT_APP_ORCHESTRATOR_URL_SUFFIX;
    if (!orchestratorUrlSuffix) {
      setError("Dashboard URL is not configured correctly.");
      return;
    }
    const dashboardUrl = `https://pod-plus-dashboard${orchestratorUrlSuffix}`;
    const reportUrl = `${dashboardUrl}/report/${simId}/${vtcId}`;
    window.open(reportUrl, '_blank', 'noopener,noreferrer');
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'completed_full':
        return { color: 'green', fontWeight: 'bold' };
      case 'completed_partial':
        return { color: 'orange', fontWeight: 'bold' };
      case 'stalled':
        return { color: 'red', fontWeight: 'bold' };
      case 'interviewing':
        return { color: 'blue', fontWeight: 'bold' };
      default:
        return { fontWeight: 'bold' };
    }
  };

  const allTeamMembersProvidedInput = () => {
    if (!jd.teamMembers || jd.teamMembers.length === 0) {
      return true;
    }
    return jd.teamMembers.every(member => member.status === 'input_provided');
  };

  const handleDeleteProject = async () => {
    try {
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/deleteJobDescription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ jdId }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete project.');
      }

      // Redirect to welcome page on success
      window.location.href = '/simulations'; 
    } catch (err) {
      console.error(err);
      setError(err.message);
    }
  };

  return (
    <div className="li-layout">
      <div className="li-container">
        
        {/* Left Sidebar */}
        <aside className="li-sidebar-left">
           <div className="li-card li-profile-card">
              <div className="li-card-background" style={{ backgroundColor: '#a0b4b7' }}></div>
              <div className="li-card-content">
                 <img 
                    src={userProfile?.avatar_url || defaultAvatar} 
                    alt="Profile" 
                    className="li-avatar"
                 />
                 <div className="li-identity">
                    <h3 className="li-name">{userProfile?.firstName} {userProfile?.lastName}</h3>
                    <p className="li-headline">{userProfile?.headline || 'Hiring Manager'}</p>
                 </div>
              </div>
           </div>
           
           {createdJds && createdJds.length > 0 && (
               <div className="li-card">
                  <div className="li-card-header" style={{ padding: '12px 12px 4px', backgroundColor: '#eef3f8' }}>
                      <h3 style={{ fontSize: '14px', color: '#000000' }}>{t('welcome.hmJdListTitle')}</h3>
                  </div>
                  <ul className="li-list-groups">
                      {createdJds.map(cj => (
                          <li key={cj.id} onClick={() => { if(cj.id !== jdId) window.location.href = `/hm-tasks/${cj.id}`; }} style={{ padding: '8px 12px', backgroundColor: cj.id === jdId ? '#f3f2ef' : 'transparent' }}>
                                                                <div style={{ flex: 1 }}>
                                                                    <div style={{ fontWeight: '600', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{cj.title}</div>
                                                                    <div style={{ fontSize: '11px', color: 'rgba(0,0,0,0.6)' }}>
                                                                        {t(`status.${jdStatuses[cj.id] || 'readyForLaunch'}`, jdStatuses[cj.id] || 'Ready for launch')}
                                                                    </div>
                                                                </div>                          </li>
                      ))}
                  </ul>
               </div>
           )}
        </aside>

        {/* Main Feed */}
        <main className="li-main-feed">
           {error && <div className="li-card li-error-msg" style={{ padding: '16px' }}>{error}</div>}
           {!jd && !error && <div className="li-card" style={{ padding: '16px' }}>{t('loading')}</div>}

           {jd && (
             <>
               <div className="li-card" style={{ padding: '16px' }}>
                   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                       <h1 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>{t('hmTaskPage.title', { title: jd.title })}</h1>
                       {userProfile?.roles?.includes('admin') && (
                           <button 
                               onClick={() => { if (window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) handleDeleteProject(); }}
                               style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#cc1016' }}
                               title="Delete Project"
                           >
                               <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                           </button>
                       )}
                   </div>
                   <div style={{ fontSize: '12px', color: 'rgba(0,0,0,0.6)' }}>
                      {jd.location || 'Remote'} • {jd.type || 'Full-time'}
                   </div>
               </div>

               <div className="li-card" style={{ padding: '16px' }}>
                   <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>{t('hmTaskPage.jobDetails')}</h2>
                   <p style={{ fontSize: '14px', lineHeight: '1.5' }}>{jd.summary || t('hmTaskPage.noSummary')}</p>
               </div>

               <div className="li-card" style={{ padding: '16px' }}>
                   <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>{t('hmTaskPage.teamStatusTitle')}</h2>
                   {jd.teamMembers && jd.teamMembers.length > 0 ? (
                      <ul style={{ listStyle: 'none', padding: 0 }}>
                        {jd.teamMembers.map(member => (
                          <li key={member.uid} style={{ padding: '8px 0', borderBottom: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontSize: '14px', fontWeight: '500' }}>{member.email}</div>
                            <div style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '4px', backgroundColor: member.status === 'input_provided' ? '#e6f7ee' : '#fff3cd', color: member.status === 'input_provided' ? '#057642' : '#856404' }}>
                               {t(`startHiring.status.${member.status.replace('_', '')}`)}
                            </div>
                          </li>
                        ))}
                      </ul>
                   ) : (
                      <p style={{ fontSize: '14px', color: '#666' }}>{t('welcome.hmNoTeamMembers')}</p>
                   )}
               </div>

               <div className="li-card" style={{ padding: '16px' }}>
                   <h2 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>{t('hmTaskPage.simulationStatus')}</h2>
                   <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                       <div style={{ fontSize: '14px', color: '#666' }}>
                           {simulations.length > 0 
                             ? "Simulation has been launched."
                             : (!jd.teamMembers || jd.teamMembers.length === 0)
                               ? "No team members assigned. Simulation is ready to launch."
                               : (allTeamMembersProvidedInput() ? t('startHiring.allInputProvided') : t('startHiring.waitingForInput'))
                           }
                       </div>
                       <button 
                          onClick={handleLaunchSimulation} 
                          className="li-btn-primary" 
                          disabled={!jd.title || !allTeamMembersProvidedInput() || isLaunching || simulations.length > 0}
                       >
                          {isLaunching ? t('startHiring.launching') : (simulations.length > 0 ? t('startHiring.launched') : t('startHiring.launchButton'))}
                       </button>
                   </div>
               </div>

               {simulations.length > 0 && simulations.map(sim => (
                  <div key={sim.id} className="li-card" style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                          <h2 style={{ fontSize: '16px', fontWeight: '600' }}>{t('hmTaskPage.resultsTitle')}</h2>
                          <span style={{ fontSize: '12px', color: '#666' }}>{sim.created_at?.toDate().toLocaleString()}</span>
                      </div>
                      
                      <div style={{ marginBottom: '16px', fontSize: '14px' }}>
                          <strong>Status: </strong> <span style={getStatusStyle(sim.status)}>{t(`status.${sim.status}`, sim.status)}</span>
                      </div>

                      {sim.candidates && Object.keys(sim.candidates).length > 0 && (
                          <div style={{ marginTop: '16px' }}>
                              <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>{t('hmTaskPage.candidatesTitle')}</h4>
                              {Object.entries(sim.candidates).map(([vtcId, candidateData]) => (
                                  <div key={vtcId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px', backgroundColor: '#f9fafb', borderRadius: '4px', marginBottom: '8px' }}>
                                      <div style={{ fontSize: '14px' }}>
                                          <span style={{ fontWeight: '600' }}>{candidateData.name || vtcId.substring(0, 8)}...</span>
                                          <span style={{ marginLeft: '8px', fontSize: '12px', padding: '2px 6px', borderRadius: '4px', backgroundColor: candidateData.status === 'shortlisted' ? '#e6f7ee' : '#f3f2ef', color: candidateData.status === 'shortlisted' ? '#057642' : '#666' }}>
                                              {candidateData.status}
                                          </span>
                                      </div>
                                      {candidateData.status === 'shortlisted' && (
                                          <button onClick={() => handleGenerateReport(sim.id, vtcId)} className="li-btn-outline-primary" style={{ width: 'auto', padding: '4px 12px', fontSize: '12px' }}>
                                              View Report
                                          </button>
                                      )}
                                  </div>
                              ))}
                          </div>
                      )}
                  </div>
               ))}
             </>
           )}
        </main>

        {/* Right Sidebar */}
        <aside className="li-sidebar-right">
            <div className="li-card li-promo-card">
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>{t('hmTaskPage.quickActions')}</div>
                <Link to="/start-hiring" className="li-btn-outline-primary" style={{ width: '100%', textAlign: 'center' }}>
                    Start New Hiring Project
                </Link>
            </div>
            <div className="li-card li-promo-card">
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>{t('hmTaskPage.quickTips')}</div>
                <p style={{ fontSize: '13px', color: '#666', lineHeight: '1.4' }}>
                    {t('hmTaskPage.quickTipCultureInput')}
                </p>
            </div>
            {/* New card for Check All Simulations */}
            <div className="li-card li-promo-card">
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>{t('hmTaskPage.checkAllSimulationsTitle')}</div>
                <p style={{ fontSize: '13px', color: '#666', lineHeight: '1.4', marginBottom: '12px' }}>
                    {t('hmTaskPage.checkAllSimulationsSubtitle')}
                </p>
                <Link to="/simulations" className="li-btn-outline-primary" style={{ width: '100%', textAlign: 'center' }}>
                    {t('hmTaskPage.viewAllSimulationsButton')}
                </Link>
            </div>
            <div className="li-footer-links">
                <Link to="/about">{t('footer.about')}</Link>
                <Link to="/accessibility">{t('footer.accessibility')}</Link>
                <Link to="/help-center">{t('footer.helpCenter')}</Link>
                <Link to="/privacy-terms">{t('footer.privacyTerms')}</Link>
                <span>{t('auth.footer.rights')}</span>
            </div>
        </aside>
      </div>
    </div>
  );
}

export default HmTaskPage;